import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Toggle } from 'rsuite';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faEdit,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { updateCorporateInvoiceId } from '../../api/Purchase';
import styles from './CorporateInvoiceIdEditor.module.scss';

// Backend returns RFC7807 problem-detail bodies on 4xx - the useful bit is
// `detail`, not `message`. Map the two documented codes to something
// readable and fall back to whatever the server sent otherwise.
const getErrorMessage = (error, oldValue, purchaseId) => {
  const detail = error?.detail;
  if (detail === 'PURCHASE_NOT_FOUND_FOR_CORPORATE_INVOICE_ID') {
    return `No purchases found with invoice number "${oldValue}"`;
  }
  if (detail === 'PURCHASE_NOT_FOUND') {
    return `Purchase ${purchaseId} not found`;
  }
  return detail || error?.message || 'Failed to update invoice number';
};

// Inline editor for a purchase's corporate invoice number, shared by the
// Pending Purchases and View Vouchers drawers. Saving goes through a confirm
// step because, when the purchase already has an invoice number, the user can
// choose to rename it on every purchase sharing that number.
//
// Whether the field is in edit mode is owned by the parent (isEditing /
// onEditStart / onEditEnd) so it stays in sync with the parent's other
// editable fields - only one field is edited at a time.
const CorporateInvoiceIdEditor = ({
  value,
  purchaseId,
  isPendingVoucher,
  token,
  isEditing,
  onEditStart,
  onEditEnd,
  onUpdated,
}) => {
  const currentValue = value || '';
  const [draft, setDraft] = useState(currentValue);
  const [confirming, setConfirming] = useState(false);
  const [updateAll, setUpdateAll] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset the local edit state whenever the parent leaves edit mode or the
  // underlying record changes (e.g. a different row is opened).
  useEffect(() => {
    setDraft(currentValue);
    setConfirming(false);
    setUpdateAll(false);
  }, [isEditing, currentValue, purchaseId]);

  const newValue = draft.trim();
  const isUnchanged = newValue === currentValue;

  const handleRequestSave = () => {
    if (!newValue || isUnchanged) {
      onEditEnd();
      return;
    }
    setUpdateAll(false);
    setConfirming(true);
  };

  const handleConfirm = async () => {
    const payload = updateAll
      ? {
        isPendingVoucher,
        isUpdateAllCorporateInvoiceId: true,
        purchaseId: '',
        oldCorporateInvoiceId: currentValue,
        newCorporateInvoiceId: newValue,
      }
      : {
        isPendingVoucher,
        isUpdateAllCorporateInvoiceId: false,
        purchaseId,
        oldCorporateInvoiceId: '',
        newCorporateInvoiceId: newValue,
      };

    setSaving(true);
    try {
      const response = await updateCorporateInvoiceId(payload, token);
      setSaving(false);

      if (response.message === 'CORPORATE_INVOICE_ID_UPDATED_SUCCESSFULLY') {
        toast.success(
          `Invoice number updated on ${response.updatedPurchaseIds?.length || 0
          } purchase(s)`
        );
        onUpdated(newValue, response);
        onEditEnd();
      } else {
        toast.error('Failed to update invoice number');
      }
    } catch (error) {
      setSaving(false);
      toast.error(getErrorMessage(error, currentValue, purchaseId));
    }
  };

  if (isEditing && confirming) {
    return (
      <div className={styles.confirm}>
        <div className={styles.confirm_message}>
          Set invoice number to <strong>{newValue}</strong>.
        </div>
        {currentValue && (
          <div className={styles.update_all}>
            <Toggle
              checked={updateAll}
              disabled={saving}
              onChange={(checked) => setUpdateAll(checked)}
            />
            <span className={styles.update_all_label}>
              Update all purchases on invoice "{currentValue}"
            </span>
          </div>
        )}
        <div className={styles.confirm_actions}>
          <button
            type='button'
            className='btn btn-sm btn-primary'
            disabled={saving}
            onClick={handleConfirm}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Updating...
              </>
            ) : (
              'Confirm'
            )}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-outline-secondary'
            disabled={saving}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <span className={styles.field}>
        <input
          type='text'
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className={styles.input}
          autoFocus
        />
        <span className={styles.edit_actions}>
          <i
            className='text-primary'
            style={{
              cursor: isUnchanged ? 'not-allowed' : 'pointer',
              opacity: isUnchanged ? 0.5 : 1,
            }}
            title={isUnchanged ? 'No changes to save' : 'Save'}
            onClick={isUnchanged ? undefined : handleRequestSave}
          >
            <FontAwesomeIcon icon={faCheck} />
          </i>
          <i
            className='text-primary'
            style={{ cursor: 'pointer' }}
            title='Cancel'
            onClick={onEditEnd}
          >
            <FontAwesomeIcon icon={faTimes} style={{ color: 'red' }} />
          </i>
        </span>
      </span>
    );
  }

  return (
    <span className={styles.field}>
      <span className={styles.value}>{currentValue || 'N/A'}</span>
      <button
        type='button'
        className={`btn btn-link p-0 m-0 align-baseline ${styles.edit_button}`}
        onClick={onEditStart}
        disabled={!purchaseId}
        title={purchaseId ? 'Edit invoice number' : 'No purchase linked'}
      >
        <FontAwesomeIcon icon={faEdit} />
      </button>
    </span>
  );
};

CorporateInvoiceIdEditor.propTypes = {
  value: PropTypes.string,
  purchaseId: PropTypes.string,
  isPendingVoucher: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEditStart: PropTypes.func.isRequired,
  onEditEnd: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
};

export default CorporateInvoiceIdEditor;
